import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import L from "leaflet";
import { useEffect, useRef, useState } from "react";
import "leaflet/dist/leaflet.css";
import somaliaGeoJSON from "../../assets/som_map.geojson";
import * as turf from '@turf/turf';


interface MapperProps {
  onRegionClick: (region: string, stats: RegionStats) => void;
}

type EvictionRecord = {
  evictedfromregion: string;
  evictedfromdistrict?: string;
  year: number;
  month: string;
  evictedindividuals: number;
  evictedhouseholds: number;
  evictedreason?: string;
};

interface RegionStats {
  evictedindividuals: number;
  evictedhouseholds: number;
  totalProtectedIndividuals: number;

  evictionsByRegionPerYear: Record<string, { evictions: number }>;
  evictionsByRegionPerMonth: Record<
    string,
    Record<
      string,
      {
        total_evictions_individuals: number;
        total_evictions_households: number;
      }
    >
  >;
  evictionsByYearPerMonth: Record<
    string,
    Record<
      string,
      {
        total_evictions_individuals: number;
        total_evictions_households: number;
      }
    >
  >;
  preventionsByYearPerMonth: Record<
    string,
    Record<
      string,
      {
        total_evictions_individuals: number;
        total_evictions_households: number;
      }
    >
  >;
  preventionsByRegionPerMonth: Record<string, number>;

  evictionsByReasonsPerYear: Record<
    string,
    Record<
      string,
      {
        total_evictions_individuals: number;
        total_evictions_households: number;
      }
    >
  >;
  evictionsByRegionPerReason: Record<string, Record<string, number>>;
  evictionsByRegionPerYearByReason: Record<
    string,
    Record<
      string,
      Record<string, { total_evictions_individuals: number }>
    >
  >;

  overallEvictionsByYear: Record<string, number>;
  overallEvictionsByMonth: Record<string, number>;
  preventionsByRegionPerYear: Record<string, number>;
  overallPreventionsByYear: Record<string, number>;
  overallPreventionsByMonth: Record<string, number>;

  overallEvictedRegions: Record<string, number>;
  overallEvictionReasons: Record<string, number>;
  overallEvictedDistricts: Record<string, number>;
}

const Mapper: React.FC<MapperProps> = ({ onRegionClick }) => {
  const mapRef = useRef<any>(null);
  const [geoData, setGeoData] = useState<any>(null);
  const [evictionStats, setEvictionStats] = useState<Record<string, RegionStats>>({});
  const [topReasonsOverall, setTopReasonsOverall] = useState<[string, number][]>([]);
  const [totalEvictedIndividuals, setTotalEvictedIndividuals] = useState<number>(0);
  const [totalEvictedHouseholds, setTotalEvictedHouseholds] = useState<number>(0);
  const [topDistrictsOverall, setTopDistrictsOverall] = useState<[string, number][]>([]); // Track top districts
  const [topRegionsOverall, setTopRegionsOverall] = useState<[string, number][]>([]); // Track top regions
  const [evictionsPerRegionByYear, setEvictionsPerRegionByYear] = useState<[string, number][]>([]);
  const [evictionsPerRegionByMonth, setEvictionsPerRegionByMonth] = useState<[string, number][]>([]);
  const [evictionsByMonth, setEvictionsByMonth] = useState<[string, number][]>([]);
  const [evictionsByYearPerMonth, setEvictionsByYearPerMonth] = useState<Record<string, Record<string, number>>>({});

  const formatNumber = (num: number) => num.toLocaleString();

  // Load GeoJSON data
  useEffect(() => {
    fetch(somaliaGeoJSON)
      .then((res) => res.json())
      .then((data) => setGeoData(data))
      .catch((error) => console.error("Error loading GeoJSON:", error));
  }, []);

  const normalizeRegionName = (name: string): string => {
    if (!name) return name;
    return name.replace(/["']/g, "").trim();
  };

  useEffect(() => {
    fetch("http://localhost:3300/api/evictions/")
      .then((res) => res.json())
      .then((data: EvictionRecord[]) => {
        if (!Array.isArray(data) || data.length === 0) {
          console.error("API response is empty or invalid");
          return;
        }
  
        const stats: Record<string, RegionStats> = {};
        const overallEvictionsPerYearPerMonth: Record<string, Record<string, number>> = {};
        const overallEvictedRegions: Record<string, number> = {};
        const overallEvictionsbyYear: Record<string, number> = {};
        
        let overallEvictedIndividuals = 0;
        let overallEvictedHouseholds = 0;
        const evictionReasons: Record<string, number> = {};
        const evictionDistricts: Record<string, number> = {};
        
        data.forEach((record) => {
          const { evictedfromregion, evictedfromdistrict, evictedindividuals, evictedhouseholds, evictedreason, year, month } = record;
  
          if (!evictedfromregion || !year || !month) return;
  
          const cleanedRegion = evictedfromregion.trim();
          const cleanedYear = String(year);
          const cleanedMonth = month.trim();
  
          // Initialize region stats
          if (!stats[cleanedRegion]) {
            stats[cleanedRegion] = {
              evictedindividuals: 0,
              evictedhouseholds: 0,
              totalProtectedIndividuals: 0,
          
              evictionsByRegionPerYear: {},
              evictionsByRegionPerMonth: {},
              evictionsByYearPerMonth: {},
              preventionsByYearPerMonth: {},
              preventionsByRegionPerMonth: {},
          
              evictionsByReasonsPerYear: {},
              evictionsByRegionPerReason: {},
              evictionsByRegionPerYearByReason: {},
          
              overallEvictionsByYear: {},
              overallEvictionsByMonth: {},
              preventionsByRegionPerYear: {},
              overallPreventionsByYear: {},
              overallPreventionsByMonth: {},
          
              overallEvictedRegions: {},
              overallEvictionReasons: {},
              overallEvictedDistricts: {},
            };
          }
          
  
          // Update eviction counts
          stats[cleanedRegion].evictedindividuals += evictedindividuals;
          stats[cleanedRegion].evictedhouseholds += evictedhouseholds;
  
          
  
          // Track overall eviction counts
          overallEvictedIndividuals += evictedindividuals;
          overallEvictedHouseholds += evictedhouseholds;
          overallEvictedRegions[cleanedRegion] = 
            (overallEvictedRegions[cleanedRegion] || 0) + evictedindividuals;
  
          overallEvictionsbyYear[cleanedYear] = 
            (overallEvictionsbyYear[cleanedYear] || 0) + evictedindividuals;
  
          // Track eviction reasons
          if (evictedreason) {
            evictionReasons[evictedreason] = (evictionReasons[evictedreason] || 0) + evictedindividuals;
          }
  
          // Track eviction districts
          if (evictedfromdistrict) {
            evictionDistricts[evictedfromdistrict] = (evictionDistricts[evictedfromdistrict] || 0) + evictedindividuals;
          }
        });
  
        // Update state in one batch
        //setEvictionStats(stats);
        setTotalEvictedIndividuals(overallEvictedIndividuals);
        setTotalEvictedHouseholds(overallEvictedHouseholds);
        setTopReasonsOverall(Object.entries(evictionReasons).sort((a, b) => b[1] - a[1]).slice(0, 5));
        setTopDistrictsOverall(Object.entries(evictionDistricts).sort((a, b) => b[1] - a[1]).slice(0, 5));
        setTopRegionsOverall(Object.entries(overallEvictedRegions).sort((a, b) => b[1] - a[1]).slice(0, 5));
  
      })
      .catch((error) => console.error("Error fetching eviction data:", error));
  }, []);
  


 
  // Auto-zoom to Somalia when GeoJSON loads
  useEffect(() => {
    if (geoData && mapRef.current) {
      const map = mapRef.current;
      const geoJsonLayer = L.geoJSON(geoData);
      map.fitBounds(geoJsonLayer.getBounds());
    }
  }, [geoData]);

  // Handle region click and send stats to Home
  const onEachFeature = (feature: any, layer: L.Layer) => {
    const regionName = feature.properties.NAME_1?.trim() || "UNKNOWN";

    const polygonLayer = layer as L.Polygon;

    const geoJson = polygonLayer.toGeoJSON() as GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>;
    const centerPoint = turf.centerOfMass(geoJson);
    const [lng, lat] = centerPoint.geometry.coordinates;
    const centroid = new L.LatLng(lat, lng);

    let label: L.Marker | null = null;

    polygonLayer.on('mouseover', () => {
      const zoom = mapRef.current.getZoom();
      const fontSize = Math.max(8, zoom * 1.2);

      label = L.marker(centroid, {
        icon: L.divIcon({
          html: `<div style="
        font-weight: bold;
        font-size: ${fontSize}px;
        color: #333;
        text-shadow: 1px 1px 2px white;
        white-space: nowrap;
        text-align: center;
      ">${regionName}</div>`,
          className: '',
          iconSize: [0, 0]
        }),
        interactive: false
      });

      label.addTo(mapRef.current);
    });

    polygonLayer.on('mouseout', () => {
      if (label) {
        mapRef.current.removeLayer(label);
        label = null;
      }
    });



    const ENABLE_CLICKS = true; // toggle this to true if needed

    if (ENABLE_CLICKS) {
    layer.on({
      click: (e: L.LeafletMouseEvent) => {
        const stats = evictionStats[regionName] || {
          evictedindividuals: 0,
          evictedhouseholds: 0,
          evictedreason: {},
          evictionsByRegionPerYear: {},
          evictionsByRegionPerMonth: {},
          evictionsByMonth: {},
          evictionsByRegion: {},
        };

        onRegionClick(regionName, stats);

      

      
        // Get top 5 eviction reasons overall
        const overallReasonsHTML = topReasonsOverall.length
          ? `<ul>${topReasonsOverall.map(([reason, count]) => `<li>${reason} (${count})</li>`).join("")}</ul>`
          : "No recorded reasons.";

        // Get top 5 districts overall (ignoring region)
        const overallDistrictsHTML = topDistrictsOverall.length
          ? `<ul>${topDistrictsOverall.map(([district, count]) => `<li>${district} (${count} individuals)</li>`).join("")}</ul>`
          : "No recorded districts.";

        // Get top 5 evicted regions overall
        const topRegionsHTML = topRegionsOverall.length
          ? `<ul>${topRegionsOverall.map(([region, count]) => `<li>${region} (${count} individuals)</li>`).join("")}</ul>`
          : "No recorded regions.";

        // 🔹 Get per-region evictions by year
        const regionEvictionsByYearHTML = Object.entries(stats.evictionsByRegionPerYear).length
          ? `<ul>${Object.entries(stats.evictionsByRegionPerYear)
            .sort((a, b) => a[0].localeCompare(b[0])) // Sort by year
            .map(([year, count]) => `<li>${year}: ${count} individuals</li>`)
            .join("")}</ul>`
          : "No data available.";

        // 🔹 Get per-region evictions by year
        const regionEvictionsByRegionMonthHTML = Object.entries(stats.evictionsByRegionPerMonth).length
          ? `<ul>${Object.entries(stats.evictionsByRegionPerMonth)
            .sort((a, b) => a[0].localeCompare(b[0])) // Sort by year
            .map(([year, count]) => `<li>${year}: ${count} individuals</li>`)
            .join("")}</ul>`
          : "No data available.";

        // 🔹 Get overall evictions by year
        const overallEvictionsByYearHTML = evictionsPerRegionByYear.length
          ? `<ul>${evictionsPerRegionByYear
            .map(([year, count]) => `<li>${year}: ${count} individuals</li>`)
            .join("")}</ul>`
          : "No data available.";

        // Sort months in correct order (Jan -> Dec)
        const sortedMonths = evictionsByMonth.sort(
          (a, b) => new Date(`1 ${a[0]} 2000`).getMonth() - new Date(`1 ${b[0]} 2000`).getMonth()
        );
        // Convert to HTML list
        const evictionsByMonthHTML = sortedMonths.length
          ? `<ul>${sortedMonths.map(([month, count]) => `<li>${month}: ${count} individuals</li>`).join("")}</ul>`
          : "No monthly data available.";

        const evictionsByYearPerMonthHTML = Object.entries(evictionsByYearPerMonth).length
          ? `<ul>${Object.entries(evictionsByYearPerMonth)
            .map(([year, months]) =>
              `<li><strong>${year}</strong>: 
          <ul>${Object.entries(months)
                .map(([month, count]) => `<li>${month}: ${count} individuals</li>`)
                .join("")}
          </ul>
        </li>`
            )
            .join("")}</ul>`
          : "No data available.";

        const popupContent = `    
        <strong>Region:</strong> ${regionName}<br/>
        <!--
        <strong>Evictions (Overall):</strong> ${formatNumber(totalEvictedIndividuals)}<br/>
          <strong>Households (Overall):</strong> ${formatNumber(totalEvictedHouseholds)}<br/>
        
        <strong>Year (Region):</strong> ${regionEvictionsByRegionMonthHTML}<br/>
        
        <strong>Evictions by Year (Region):</strong> ${regionEvictionsByRegionMonthHTML}<br/> 
        
          <strong>Evictions by Year (Region):</strong> ${regionEvictionsByRegionMonthHTML}<br/>
          <strong>Year/ Month:</strong> ${evictionsByYearPerMonthHTML}<br/>
          <strong>Year/ Month:</strong> ${evictionsByYearPerMonthHTML}<br/>
          <strong>Evictions by Year (Region):</strong> ${regionEvictionsByRegionMonthHTML}<br/>
          <strong>Evictions by Year (Region):</strong> ${regionEvictionsByYearHTML}<br/>
          <hr/>
          <strong>Evictions by Month (Overall):</strong> ${evictionsByMonthHTML}  
          <strong>Evictions by Year (Overall):</strong> ${overallEvictionsByYearHTML}<br/>
          <hr/>
          <strong>Top 5 Evicted Regions (Overall):</strong> ${topRegionsHTML}<br/>
          <hr/>
          <strong>Evictions (Region):</strong> ${formatNumber(stats.evictedindividuals)}<br/>
          <strong>Households (Region):</strong> ${formatNumber(stats.evictedhouseholds)}<br/>
          <hr/>
          <strong>Evictions (Overall):</strong> ${formatNumber(totalEvictedIndividuals)}<br/>
          <strong>Households (Overall):</strong> ${formatNumber(totalEvictedHouseholds)}<br/>
          <strong>Top Eviction Reasons (Overall):</strong> ${overallReasonsHTML} 
          <hr/>
          <strong>Top 5 Evicted Districts (Overall):</strong> ${overallDistrictsHTML}-->
        `;
      },
    });
  }
  };

  return (
    <MapContainer ref={mapRef} style={{ height: "60vh", width: "100%" }} center={[5.1521, 46.1996]} zoom={6} className="leaflet-container">
      <TileLayer
        url="https://stamen-tiles.a.ssl.fastly.net/toner-lite/{z}/{x}/{y}.png"
        attribution="Map tiles by <a href='http://stamen.com'>Stamen Design</a>, <a href='http://openstreetmap.org'>OpenStreetMap</a> contributors"
      />
      {geoData && <GeoJSON data={geoData} style={{ color: "white", weight: 0.8, fillColor: "#1E90FF", fillOpacity: 0.5 }} onEachFeature={onEachFeature} />}
    </MapContainer>
  );
};

export default Mapper;